import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  FileCheck2,
  FileText,
  Gauge,
  LayoutDashboard,
  RefreshCcw,
  ShieldCheck,
  UploadCloud,
} from "lucide-react";
import SalesAffiliateLayout from "../components/SalesAffiliateLayout";
import { useSalesAffiliateWorkspace } from "../context/SalesAffiliateWorkspaceContext";
import { SALES_AFFILIATE_WORKSPACE } from "../lib/salesAffiliateWorkspaceConfig";

const STAGES = [
  {
    number: "01",
    title: "Subscriber Dashboard",
    icon: LayoutDashboard,
    image: "/sales-affiliate/subscriber-experience/01-dashboard.png",
    fallback: ["Assessment starting point", "Current activity and audit context", "Access to Reports and ongoing work"],
    sees: "The subscriber’s operating home for AFP, including assessment activity and access to ongoing risk-management work.",
    does: "Starts or resumes an Assessment and returns to results, Reports, and Remediation as work progresses.",
    matters: "AFP is designed as an ongoing governance workflow, not merely a one-time questionnaire.",
  },
  {
    number: "02",
    title: "The 60-Question Assessment",
    icon: BookOpen,
    image: "/sales-affiliate/subscriber-experience/02-assessment.png",
    fallback: ["60 control questions", "YES / NO / UNSURE answers", "Executive help when a question is unclear"],
    sees: "One of AFP’s 60 control questions, with YES, NO, and UNSURE answer choices and plain-English executive assistance.",
    does: "Works through the questions with the people inside the organization who know the relevant systems, controls, policies, and practices.",
    matters: "AFP helps identify where controls are in place, where meaningful gaps exist, and where effectiveness or applicability still needs to be verified.",
  },
  {
    number: "03",
    title: "How AFP Turns Answers Into Risk Insight",
    icon: Gauge,
    image: "/sales-affiliate/subscriber-experience/03-results.png",
    fallback: ["Control Coverage", "Risk Severity and Risk Grade", "Findings weighted by seriousness"],
    sees: "A completed-assessment view showing measures such as Control Coverage, Risk Severity, Risk Grade, and findings.",
    does: "Reviews the organization’s current control position and the relative seriousness of identified issues.",
    matters: "AFP does not simply count answers. Its certified weighted-severity model gives greater significance to more serious control deficiencies.",
  },
  {
    number: "04",
    title: "Reports and Executive Insight",
    icon: FileText,
    image: "/sales-affiliate/subscriber-experience/04-reports.png",
    fallback: ["Risk Grade", "Critical Open and Findings Resolved", "Risk drivers and exposure information"],
    sees: "Executive-oriented reporting that converts assessment results into decision-useful risk information.",
    does: "Uses Reports to understand priorities, communicate internally, and decide where corrective attention is most important.",
    matters: "AFP turns assessment answers into practical management information rather than leaving the subscriber with only a questionnaire score.",
  },
  {
    number: "05",
    title: "Remediation Hub",
    icon: ShieldCheck,
    image: "/sales-affiliate/subscriber-experience/05-remediation.png",
    fallback: ["Open and in-progress findings", "Mapped remediation guidance", "Mitigated and closed status tracking"],
    sees: "Structured remediation work associated with identified findings and unresolved control uncertainty.",
    does: "Reviews corrective actions, updates finding status, and works through the steps needed to address identified issues.",
    matters: "AFP helps move the subscriber from diagnosis to action by connecting risk findings to organized remediation work.",
  },
  {
    number: "06",
    title: "Documenting Remediation — Building Evidence for Defensibility",
    icon: UploadCloud,
    image: "/sales-affiliate/subscriber-experience/06-evidence.png",
    fallback: ["Evidence & Documentation", "Supporting documents and links", "Evidence & Defense reporting"],
    sees: "Evidence and Documentation tools associated with remediation findings, including document uploads and supporting links.",
    does: "Preserves materials such as policies, approvals, testing records, screenshots, monitoring evidence, and other documentation showing corrective actions taken.",
    matters: "A documented remediation record can support regulatory defensibility by helping demonstrate that identified risks were addressed rather than merely acknowledged.",
  },
];

function ProductionVisual({ stage }) {
  const [failed, setFailed] = useState(false);
  if (!failed) {
    return (
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 shadow-sm">
        <img
          src={stage.image}
          alt={`${stage.title} — current AFP subscriber experience`}
          className="h-auto w-full object-cover"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      </div>
    );
  }

  const Icon = stage.icon;
  return (
    <div className="rounded-3xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-slate-50 p-6 text-slate-950 shadow-sm sm:p-8">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-sm ring-1 ring-blue-500/20">
          <Icon size={24} />
        </div>
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.3em] text-blue-600">Production visual</div>
          <div className="mt-1 text-lg font-black text-slate-950">{stage.title}</div>
        </div>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {stage.fallback.map((item) => (
          <div key={item} className="rounded-2xl border border-slate-200 bg-white p-4 text-sm font-semibold leading-6 text-slate-800 shadow-sm ring-1 ring-blue-50">
            {item}
          </div>
        ))}
      </div>
      <p className="mt-5 text-xs leading-5 text-slate-500">
        This branded fallback is shown only when the controlled Production screenshot asset has not yet been added to DEV.
      </p>
    </div>
  );
}

function StageCopy({ stage }) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">What the subscriber sees</div>
        <p className="mt-2 text-sm leading-6 text-slate-700">{stage.sees}</p>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">What the subscriber does</div>
        <p className="mt-2 text-sm leading-6 text-slate-700">{stage.does}</p>
      </div>
      <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
        <div className="text-[11px] font-black uppercase tracking-[0.2em] text-blue-700">Why it matters</div>
        <p className="mt-2 text-sm leading-6 text-blue-950">{stage.matters}</p>
      </div>
    </div>
  );
}

function ReassessmentCycle() {
  const items = ["Assess", "Understand Results", "Remediate", "Document Evidence", "Reassess"];
  return (
    <div className="rounded-3xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-slate-50 p-6 shadow-sm sm:p-8">
      <div className="flex items-center gap-3">
        <RefreshCcw className="text-blue-600" size={26} />
        <div className="text-xl font-black text-slate-950">Ongoing improvement cycle</div>
      </div>
      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center">
        {items.map((item, index) => (
          <React.Fragment key={item}>
            <div className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-center text-sm font-black text-slate-900 shadow-sm">{item}</div>
            {index < items.length - 1 ? <div className="text-center text-xl font-black text-blue-500">→</div> : null}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function MessagingGuardrails() {
  const canSay = [
    "Assess AI governance and control posture",
    "Identify control gaps and unresolved uncertainty",
    "Understand the relative significance of findings",
    "Organize remediation work and corrective actions",
    "Document remediation evidence that can support defensibility",
    "Reassess over time to measure progress",
  ];
  const shouldNotSay = [
    "AFP guarantees legal compliance",
    "AFP guarantees that a fine or enforcement action will be avoided",
    "AFP guarantees a particular regulatory or legal outcome",
    "AFP replaces legal advice or legal counsel",
    "A particular AFP score proves an organization is compliant or safe",
  ];
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6">
        <div className="flex items-center gap-2 text-emerald-950"><CheckCircle2 size={22} /><h3 className="text-lg font-black">What you can say</h3></div>
        <ul className="mt-4 space-y-3 text-sm leading-6 text-emerald-950">
          {canSay.map((item) => <li key={item} className="flex gap-2"><span className="font-black">✓</span><span>{item}</span></li>)}
        </ul>
      </div>
      <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6">
        <div className="flex items-center gap-2 text-amber-950"><FileCheck2 size={22} /><h3 className="text-lg font-black">What you should not say</h3></div>
        <ul className="mt-4 space-y-3 text-sm leading-6 text-amber-950">
          {shouldNotSay.map((item) => <li key={item} className="flex gap-2"><span className="font-black">—</span><span>{item}</span></li>)}
        </ul>
      </div>
    </div>
  );
}

export default function SalesAffiliateSubscriberExperience() {
  const { loading, statusLoadFailed, errorMessage, configuration, refreshWorkspace } = useSalesAffiliateWorkspace();

  if (loading) {
    return (
      <SalesAffiliateLayout activeSection="learning-center">
        <div className="flex min-h-[500px] items-center justify-center">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 shadow-sm">Loading subscriber experience walkthrough…</div>
        </div>
      </SalesAffiliateLayout>
    );
  }

  if (statusLoadFailed) {
    return (
      <SalesAffiliateLayout activeSection="learning-center">
        <div className="mx-auto flex min-h-[500px] max-w-2xl items-center px-8">
          <div className="w-full rounded-3xl border border-amber-200 bg-amber-50 p-6 text-amber-950 shadow-sm">
            <div className="text-sm font-black">Walkthrough unavailable</div>
            <p className="mt-2 text-sm leading-6">We could not safely confirm your Sales Affiliate access.</p>
            {errorMessage ? <p className="mt-2 text-xs opacity-80">{errorMessage}</p> : null}
            <button type="button" onClick={() => refreshWorkspace()} className="mt-4 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800">Retry</button>
          </div>
        </div>
      </SalesAffiliateLayout>
    );
  }

  const allowed = configuration.workspaceState === SALES_AFFILIATE_WORKSPACE.ONBOARDING || configuration.workspaceState === SALES_AFFILIATE_WORKSPACE.ACTIVE;
  if (!allowed) return <Navigate to={configuration.defaultPath} replace />;

  return (
    <SalesAffiliateLayout activeSection="learning-center">
      <div className="mx-auto max-w-6xl px-8 py-10">
        <Link to="/sales-affiliate/learning-center" className="inline-flex items-center gap-2 text-sm font-black text-blue-700 hover:text-blue-900"><ArrowLeft size={17} />Back to Learning Center</Link>

        <div className="mt-6 flex items-start gap-3">
          <BookOpen className="mt-1 text-blue-600" size={30} />
          <div>
            <div className="text-xs font-black uppercase tracking-[0.32em] text-blue-600">Subscriber Experience Walkthrough</div>
            <h1 className="mt-2 text-4xl font-black tracking-tight text-slate-950">What Subscribers Experience in AFP</h1>
          </div>
        </div>
        <p className="mt-5 max-w-4xl text-base leading-7 text-slate-600">This walkthrough helps you understand what AFP subscribers see, do, and receive so you can explain the platform accurately and credibly. It is an optional Learning Center resource and does not count toward Assigned Training or account activation.</p>

        <div className="mt-8 space-y-8">
          {STAGES.map((stage) => (
            <section key={stage.number} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-600 text-sm font-black text-white">{stage.number}</div>
                <h2 className="text-2xl font-black tracking-tight text-slate-950">{stage.title}</h2>
              </div>
              <div className="mt-6"><ProductionVisual stage={stage} /></div>
              <div className="mt-6"><StageCopy stage={stage} /></div>
            </section>
          ))}

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-600 text-sm font-black text-white">07</div><h2 className="text-2xl font-black tracking-tight text-slate-950">Reassessment and Ongoing Improvement</h2></div>
            <div className="mt-6"><ReassessmentCycle /></div>
            <div className="mt-6"><StageCopy stage={{ sees: "AFP as a repeatable risk-governance cycle rather than a one-time event.", does: "Returns to the platform after corrective work to reassess controls and understand how the risk position has changed.", matters: "The subscriber can use AFP over time to support continuous improvement and a more current record of AI governance activity." }} /></div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-600 text-sm font-black text-white">08</div><h2 className="text-2xl font-black tracking-tight text-slate-950">What Sales Affiliates Should Say</h2></div>
            <p className="mt-4 max-w-4xl text-sm leading-7 text-slate-600">Use the subscriber journey to explain AFP accurately. Describe what the platform helps an organization assess, understand, remediate, document, and reassess. Do not turn those capabilities into guarantees about compliance, fines, or legal outcomes.</p>
            <div className="mt-6"><MessagingGuardrails /></div>
          </section>
        </div>

        <div className="mt-8 rounded-3xl border border-blue-100 bg-blue-50 p-6 text-sm leading-7 text-blue-950">
          <strong>Remember:</strong> AI Fine Protector provides risk assessment, planning, remediation, evidence-management, and governance support. It is not legal advice and does not guarantee a particular regulatory outcome.
        </div>
      </div>
    </SalesAffiliateLayout>
  );
}
